import './global.scss' // app & third-party component styles

import { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, HashRouter, useNavigate, useLocation } from 'react-router'
import { Provider } from 'react-redux'
import { ApolloProvider } from '@apollo/client'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { Store } from 'redux'
import * as Sentry from '@sentry/react'

import { getRouterType } from 'env'
import globalHistory from './history'

import Localization from './localization'
import Router from './router'
import MaintenanceGuard from './maintenance'
import EmailEditorWindow from './pages/popup/EmailEditorWindow'
import RootErrorFallback from './components/RootErrorFallback'
import { isPopupMode } from 'services/popup/isPopupMode'
import { setEditorHostEnv } from '@owma/common-web/components/editor/ngweditor/editorEnv'

// OMD-226: 통합 editor core(공통, common-web)의 Host 의존 값을 주입한다. core 는 빌드 상수·Host
// capability 를 직접 알지 못하고 이 주입값만 읽으므로 platform·web 에서 core 파일이 byte 동일하게
// 유지된다. web(일반 브라우저): dev 판정은 `__ENV_PRODUCTION__`, 링크 클릭 guard 는 불필요(기본 false).
setEditorHostEnv({ isDev: !__ENV_PRODUCTION__ })

export default function rootRender(
  appolloClient: ApolloClient<NormalizedCacheObject>,
  store: Store<any, any>,
  history: typeof globalHistory,
): void {
  const NavigateSetter = () => {
    const navigate = useNavigate()
    const location = useLocation()

    history.navigate = navigate as any
    history.location = location as any

    const { notifyLocationChange } = history

    useEffect(() => {
      notifyLocationChange(location as any)
    }, [location, notifyLocationChange])

    return null
  }

  // 경계(Sentry.ErrorBoundary)가 못 잡는 프로바이더 바깥/초기 렌더 에러도 Sentry로 보고한다
  // (React 19는 uncaught render 에러를 re-throw 하지 않고 window.reportError 로만 흘리므로 이중 안전장치).
  // onCaughtError는 Sentry.ErrorBoundary가 이미 captureException 하므로 중복 보고 방지 위해 미설정.
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
    onUncaughtError: (error, errorInfo) => {
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack ?? undefined },
      })
    },
  })

  // popup(/popup/email-editor)은 Router/Layout/MaintenanceGuard 없이 자체 트리만 마운트한다.
  if (isPopupMode()) {
    root.render(
      <ApolloProvider client={appolloClient}>
        <Provider store={store}>
          <Localization>
            <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
              <EmailEditorWindow />
            </Sentry.ErrorBoundary>
          </Localization>
        </Provider>
      </ApolloProvider>,
    )
    return
  }

  const routerType = getRouterType()

  const CurrentRouter = routerType === 'hash' ? HashRouter : BrowserRouter

  root.render(
    <ApolloProvider client={appolloClient}>
      <CurrentRouter>
        <NavigateSetter />
        <Provider store={store}>
          <MaintenanceGuard>
            <Localization>
              <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
                <Router />
              </Sentry.ErrorBoundary>
            </Localization>
          </MaintenanceGuard>
        </Provider>
      </CurrentRouter>
    </ApolloProvider>,
  )
}
